import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { DataGrid, getGridStringOperators } from '@mui/x-data-grid';
import { Document, Page, View, Text, StyleSheet, pdf } from '@react-pdf/renderer';
import ApiService from '../../functions/apiService';
import moment from 'moment';
import { toast } from 'react-toastify';
import SelectRebuild from '../../components/Inputs/SelectRebuild';
import { ExportCsvButton } from '../../components/Buttons/ExportCsvButton';
import { FilterPanel } from '../../components/Reports/FilterPanel';
import ReportsHeader from '../../components/Reports/ReportsHeader';
import DataNotFound from '../../components/Reports/DataNotFound';
import { useDateRangeFilter } from './hooks/useDateRangeFilter';
import './reports.css';

const defaultFormValues = {
  position: null,
  shift: null,
};

const prettify = (key = '') =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const pdfStyles = StyleSheet.create({
  page: { padding: 24, fontSize: 8, fontFamily: 'Helvetica' },
  title: { fontSize: 14, marginBottom: 4, fontFamily: 'Helvetica-Bold' },
  subtitle: { fontSize: 9, marginBottom: 10, color: '#555' },
  table: { borderTopWidth: 1, borderLeftWidth: 1, borderColor: '#ddd' },
  row: { flexDirection: 'row' },
  headerRow: { backgroundColor: '#BF1D2D' },
  cell: {
    flex: 1,
    padding: 4,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#ddd',
  },
  headerCell: { color: '#ffffff', fontFamily: 'Helvetica-Bold' },
  stripe: { backgroundColor: '#f7f7f7' },
});

const VolunteerOpenShift = () => {
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const [positionOptions, setPositionOptions] = useState([]);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const { fromDate, toDate, render } = useDateRangeFilter(false, undefined, true);

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });
  const { position, shift } = watch();

  const loadPositions = () => {
    ApiService.get('/positions-list')
      .then(({ data }) => {
        const arr = (data?.data || []).map((p) => ({ id: p.SO_Value, name: p.SO_Desc }));
        setPositionOptions(arr);
      })
      .catch(() => {});
  };

  const loadShifts = () => {
    ApiService.get('/shiftTypes')
      .then(({ data }) => {
        const arr = (data?.data || []).map((s) => {
          const start = s.Start_Time ? String(s.Start_Time).slice(0, 5) : '';
          const end = s.End_Time ? String(s.End_Time).slice(0, 5) : '';
          const time = start || end ? ` (${start} - ${end})` : '';
          return { id: s.ShiftCode, name: `${s.Shift}${time}` };
        });
        setShiftOptions(arr);
      })
      .catch(() => {});
  };

  const fetchData = (format) => {
    setLoading(true);
    const payload = {
      start_date: fromDate ? moment(fromDate).format('YYYY-MM-DD') : null,
      end_date: toDate ? moment(toDate).format('YYYY-MM-DD') : null,
      position: position?.id ?? null,
      shift: shift?.id ?? null,
      csv: format === 'csv' ? 1 : 0,
      pdf: format === 'pdf' ? 1 : 0,
    };

    ApiService.get('/report/volunteer-open-shifts', payload, 'volunteer-open-shifts', format === 'csv' ? 'blob' : '')
      .then((response) => {
        setLoading(false);
        const body = response?.data;

        if (format === 'csv') {
          const blob = body instanceof Blob ? body : new Blob([body], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'volunteer-open-shifts.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }

        if (format === 'pdf') {
          const pdfUrl = body?.pdf_url || body?.data?.pdf_url;
          if (pdfUrl) {
            window.open(pdfUrl, '_blank');
          } else {
            toast.error('PDF URL not found');
          }
          return;
        }

        const list = Array.isArray(body) ? body : (body?.data?.data || body?.data || []);
        setRows((list || []).map((r, i) => ({ ...r, id: r?.id ?? r?.Id ?? `os-${i}` })));
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.data?.message || err?.response?.data?.message || 'Failed to fetch data');
      });
  };

  useEffect(() => {
    loadPositions();
    loadShifts();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const containsOnly = getGridStringOperators().filter((o) => o.value === 'contains');

  const columns =
    rows.length > 0
      ? Object.keys(rows[0])
          .filter((k) => k !== 'id')
          .map((field) => {
            const isDateField = /date/i.test(field);
            return {
              field,
              headerName: prettify(field),
              flex: 1,
              minWidth: 160,
              filterOperators: containsOnly,
              ...(isDateField
                ? {
                    valueFormatter: (params) =>
                      params?.value && moment(params.value).isValid()
                        ? moment(params.value).format('MM/DD/YYYY')
                        : (params?.value ?? ''),
                  }
                : {}),
            };
          })
      : [];

  const visibleColumns = columns.filter((c) => columnVisibilityModel[c.field] !== false);

  const cellText = (row, field) => {
    let val = row?.[field];
    if (/date/i.test(field) && val && moment(val).isValid()) {
      val = moment(val).format('MM/DD/YYYY');
    }
    return val === null || val === undefined || val === '' ? '-' : String(val);
  };

  const PdfDocument = () => (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <Text style={pdfStyles.title}>Volunteer Open Shift Report</Text>
        <Text style={pdfStyles.subtitle}>
          {`${fromDate ? moment(fromDate).format('MM/DD/YYYY') : 'All'} - ${toDate ? moment(toDate).format('MM/DD/YYYY') : 'All'}`}
          {position?.name ? `  |  Position: ${position.name}` : ''}
          {shift?.name ? `  |  Shift: ${shift.name}` : ''}
        </Text>
        <View style={pdfStyles.table}>
          <View style={[pdfStyles.row, pdfStyles.headerRow]}>
            {visibleColumns.map((c) => (
              <Text key={c.field} style={[pdfStyles.cell, pdfStyles.headerCell]}>
                {c.headerName}
              </Text>
            ))}
          </View>
          {rows.map((r, i) => (
            <View key={r.id ?? i} style={[pdfStyles.row, i % 2 ? pdfStyles.stripe : {}]} wrap={false}>
              {visibleColumns.map((c) => (
                <Text key={c.field} style={pdfStyles.cell}>
                  {cellText(r, c.field)}
                </Text>
              ))}
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );

  const exportPdf = async () => {
    setLoading(true);
    if (!rows.length) {
      toast.error('No data to export');
      return;
    }
    try {
      const blob = await pdf(<PdfDocument />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'volunteer-open-shifts.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setLoading(false);
    } catch (e) {
      toast.error('Failed to generate PDF');
      setLoading(false);
    }
  };

  const rawFilterConfig = [
    {
      key: 'position',
      label: 'Position',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={positionOptions}
          name="position"
          label="Position"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'shift',
      label: 'Shift',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={shiftOptions}
          name="shift"
          label="Shift"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={() => fetchData()}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: rows.length > 0,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton onClick={() => fetchData('csv')} text={'Export CSV'} />
          <ExportCsvButton onClick={exportPdf} text={'Export PDF'} />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper
      elevation={0}
      className="p-4"
      sx={{ marginTop: '1rem', border: '1px solid #EAECF0', borderRadius: '12px' }}
    >
      <ReportsHeader title="Volunteer Open Shift Reports" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : 4}
          />
        </Grid>

        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletVerticalWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>

        {loading && (
          <Box
            sx={{ minHeight: '100px' }}
            className="w-100 h-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && rows.length === 0 && <DataNotFound />}

        {!loading && rows.length > 0 && (
          <Box className="mt-3" sx={{ width: '100%' }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(r) => r.id}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
              pagination
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
              sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 700 } }}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default VolunteerOpenShift;
